import * as React from 'react';
import { useEffect, useState } from 'react';
import IconButton from '@mui/joy/IconButton';
import Table from '@mui/joy/Table';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import Button from "@mui/joy/Button";
import Box from "@mui/joy/Box";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Stack from "@mui/joy/Stack";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import { Chip, CircularProgress, Divider, DialogTitle, DialogContent, DialogActions } from "@mui/joy";
import { Delete, PersonAdd } from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import GeneralApiService from "../../axios/Axios";
import ResponsiveTableWrapper, { ResponsiveFilterBar, FilterBarRight } from "../ui/ResponsiveTableWrapper";

interface User {
    id: number;
    username: string;
    email: string;
    phone: string;
    role: string;
}

interface NewEmployeeData {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
}

interface EmployeeCredentials {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    password: string;
}

function ConfirmDeleteModal(props: { openState: boolean; parentCallback: (confirmed: boolean) => void; discardMessage: string }) {
    const { openState, parentCallback, discardMessage } = props;
    const [open, setOpen] = useState<boolean>(openState);

    useEffect(() => {
        setOpen(openState);
    }, [openState]);

    function actionDelete() {
        setOpen(false);
        parentCallback(true);
    }

    function actionCancel() {
        setOpen(false);
        parentCallback(false);
    }

    return (
        <React.Fragment>
            <Modal open={open} onClose={() => setOpen(false)}>
                <ModalDialog variant="outlined" role="alertdialog">
                    <DialogTitle>
                        <Delete />
                        Confirmation
                    </DialogTitle>
                    <Divider />
                    <DialogContent>
                        {discardMessage}
                    </DialogContent>
                    <DialogActions>
                        <Button variant="solid" color="danger" onClick={() => actionDelete()}>
                            Delete
                        </Button>
                        <Button variant="plain" color="neutral" onClick={() => actionCancel()}>
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </React.Fragment>
    );
}

function EmployeeCredentialsModal(props: { open: boolean; onClose: () => void; credentials: EmployeeCredentials | null }) {
    const { open, onClose, credentials } = props;

    if (!credentials) return null;

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog>
                <ModalClose />
                <Typography level="h4">Employee Created Successfully</Typography>
                <Divider />
                <Stack spacing={2} sx={{ mt: 2 }}>
                    <Box>
                        <Typography level="title-sm" sx={{ mb: 0.5 }}>Email</Typography>
                        <Input value={credentials.email} readOnly />
                    </Box>
                    <Box>
                        <Typography level="title-sm" sx={{ mb: 0.5 }}>Password</Typography>
                        <Input value={credentials.password} readOnly />
                    </Box>
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: 'sm' }}>
                        <Typography level="body-sm" sx={{ color: '#1976d2' }}>
                            📧 Please send these login credentials to the employee at {credentials.email}
                        </Typography>
                    </Box>
                    <Button onClick={onClose} sx={{ mt: 2 }}>
                        Done
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}

function Row(props: { user: User; isAdmin: boolean; onDelete: (userId: number) => void }) {
    const { user, isAdmin, onDelete } = props;
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const handleDeleteClick = () => {
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = (confirmed: boolean) => {
        if (confirmed) {
            onDelete(user.id);
        }
        setShowDeleteModal(false);
    };

    const displayName = user.username || user.email.split('@')[0];

    return (
        <>
            <tr>
                <td>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {displayName}
                        {isAdmin && <Chip size="sm" color="warning" variant="soft">Admin</Chip>}
                    </Box>
                </td>
                <td>{user.email}</td>
                <td>{user.phone}</td>
                <td>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {!isAdmin && (
                            <IconButton
                                variant="soft"
                                color="danger"
                                size="sm"
                                onClick={handleDeleteClick}
                            >
                                <Delete />
                            </IconButton>
                        )}
                    </Box>
                </td>
            </tr>
            <ConfirmDeleteModal
                openState={showDeleteModal}
                parentCallback={handleDeleteConfirm}
                discardMessage={`Are you sure you want to delete ${displayName}?`}
            />
        </>
    );
}

function AddEmployeeModal(props: { open: boolean; onClose: () => void; onSuccess: (credentials: EmployeeCredentials) => void }) {
    const { open, onClose, onSuccess } = props;
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<NewEmployeeData>({
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
    });

    const handleChange = (field: keyof NewEmployeeData) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setFormData(prev => ({ ...prev, [field]: event.target.value }));
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
            toast.error("All fields are required", { containerId: "team-table" });
            return;
        }

        setLoading(true);
        try {
            const result = await GeneralApiService.createEmployee(formData);
            toast.success("Employee added successfully!", { containerId: "team-table" });
            setFormData({ first_name: '', last_name: '', email: '', phone: '' });
            onSuccess(result); // Pass the result to show credentials
            onClose();
        } catch (error: any) {
            const errorMessage = error.response?.data?.error || error.message || "Failed to add employee";
            toast.error(errorMessage, { containerId: "team-table" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog>
                <ModalClose />
                <Typography level="h4">Add New Employee</Typography>
                <Divider />
                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <FormControl required>
                            <FormLabel>First Name</FormLabel>
                            <Input
                                value={formData.first_name}
                                onChange={handleChange('first_name')}
                                placeholder="Enter first name"
                            />
                        </FormControl>
                        <FormControl required>
                            <FormLabel>Last Name</FormLabel>
                            <Input
                                value={formData.last_name}
                                onChange={handleChange('last_name')}
                                placeholder="Enter last name"
                            />
                        </FormControl>
                        <FormControl required>
                            <FormLabel>Email</FormLabel>
                            <Input
                                type="email"
                                value={formData.email}
                                onChange={handleChange('email')}
                                placeholder="employee@example.com"
                            />
                        </FormControl>
                        <FormControl required>
                            <FormLabel>Phone</FormLabel>
                            <Input
                                type="tel"
                                value={formData.phone}
                                onChange={handleChange('phone')}
                                placeholder="+44{number} format"
                            />
                        </FormControl>
                        <Button type="submit" loading={loading}>
                            Add Employee
                        </Button>
                    </Stack>
                </form>
            </ModalDialog>
        </Modal>
    );
}

export default function TeamTable() {
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState<User[]>([]);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
    const [employeeCredentials, setEmployeeCredentials] = useState<EmployeeCredentials | null>(null);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const data = await GeneralApiService.getFranchiseUsers();
            // Extract results array from paginated response
            setUsers(data.results || []);
        } catch (error: any) {
            console.error("Failed to fetch users:", error);
            toast.error("Failed to load team members", { containerId: "team-table" });
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleDeleteUser = async (userId: number) => {
        try {
            await GeneralApiService.deleteEmployee(userId);
            toast.success("Employee deleted successfully!", { containerId: "team-table" });
            fetchUsers();
        } catch (error: any) {
            const errorMessage = error.response?.data?.error || "Failed to delete employee";
            toast.error(errorMessage, { containerId: "team-table" });
        }
    };

    const handleAddSuccess = (credentials: EmployeeCredentials) => {
        setEmployeeCredentials(credentials);
        setCredentialsModalOpen(true);
        fetchUsers();
    };

    const handleCloseCredentialsModal = () => {
        setCredentialsModalOpen(false);
        setEmployeeCredentials(null);
    };

    const admins = users.filter(user => user.role === 'admin');
    const employees = users.filter(user => user.role === 'employee');

    return (
        <>
            <ToastContainer containerId={"team-table"} position="top-right" autoClose={5000} theme="dark" />

            <ResponsiveFilterBar>
                <Box sx={{ flex: 1 }} />
                <FilterBarRight>
                    <Button
                        startDecorator={<PersonAdd />}
                        onClick={() => setAddModalOpen(true)}
                    >
                        Add Employee
                    </Button>
                </FilterBarRight>
            </ResponsiveFilterBar>

            <ResponsiveTableWrapper minWidth={600}>
                <Table aria-label="team table">
                    <thead>
                    <tr>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th aria-label="actions" />
                    </tr>
                    </thead>
                    <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                                <CircularProgress />
                            </td>
                        </tr>
                    ) : (
                        <>
                            {admins.length > 0 && (
                                <>
                                    <tr>
                                        <td colSpan={4} style={{
                                            backgroundColor: '#fff3cd',
                                            borderLeft: '4px solid #ffc107',
                                            padding: '12px'
                                        }}>
                                            <Typography level="title-sm" fontWeight="bold" sx={{ color: '#856404' }}>
                                                Administrators
                                            </Typography>
                                        </td>
                                    </tr>
                                    {admins.map((user) => (
                                        <Row
                                            key={user.id}
                                            user={user}
                                            isAdmin={true}
                                            onDelete={handleDeleteUser}
                                        />
                                    ))}
                                </>
                            )}
                            {employees.length > 0 && (
                                <>
                                    <tr>
                                        <td colSpan={4} style={{
                                            backgroundColor: '#e7f3ff',
                                            borderLeft: '4px solid #2196f3',
                                            padding: '12px'
                                        }}>
                                            <Typography level="title-sm" fontWeight="bold" sx={{ color: '#0d47a1' }}>
                                                Employees
                                            </Typography>
                                        </td>
                                    </tr>
                                    {employees.map((user) => (
                                        <Row
                                            key={user.id}
                                            user={user}
                                            isAdmin={false}
                                            onDelete={handleDeleteUser}
                                        />
                                    ))}
                                </>
                            )}
                            {users.length === 0 && (
                                <tr>
                                    <td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                                        <Typography level="body-md">No team members found</Typography>
                                    </td>
                                </tr>
                            )}
                        </>
                    )}
                    </tbody>
                </Table>
            </ResponsiveTableWrapper>

            <AddEmployeeModal
                open={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                onSuccess={handleAddSuccess}
            />

            <EmployeeCredentialsModal
                open={credentialsModalOpen}
                onClose={handleCloseCredentialsModal}
                credentials={employeeCredentials}
            />
        </>
    );
}